'use client';

import { useEffect, useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Milestone {
  id: string;
  year: string;
  title: string;
  description: string;
  icon: string;
}

export default function CompanyStory() {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate');
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -5% 0px' }
    );

    const elements = document.querySelectorAll('.animate-on-scroll');
    elements?.forEach((el) => observer?.observe(el));

    return () => observer?.disconnect();
  }, []);

  const milestones: Milestone[] = [
    {
      id: 'milestone_1',
      year: '2024',
      title: 'Foundation',
      description: 'E-GREGOR GROUP LTD was founded with a vision to revolutionize technology solutions for businesses.',
      icon: 'RocketLaunchIcon',
    },
    {
      id: 'milestone_2',
      year: '2024',
      title: 'First 50 Clients',
      description: 'Rapidly grew our client base by delivering exceptional IT support and web design services.',
      icon: 'UserGroupIcon',
    },
    {
      id: 'milestone_3',
      year: '2025',
      title: 'Service Expansion',
      description: 'Expanded our offerings to include cloud services, cybersecurity, and custom software development.',
      icon: 'CloudIcon',
    },
    {
      id: 'milestone_4',
      year: '2026',
      title: 'Industry Recognition',
      description: 'Achieved industry certifications and recognition for excellence in technology solutions.',
      icon: 'TrophyIcon',
    },
  ];

  return (
    <section className="py-20 lg:py-32 relative z-10 bg-gray-900">
      <div className="container mx-auto px-6 max-w-7xl">
        <div className="text-center mb-16 animate-on-scroll animate-in">
          <span className="text-xs uppercase tracking-[0.4em] text-blue-400 mb-4 block">
            Our Journey
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Building the Future, <span className="text-gradient-blue">One Step at a Time</span>
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto">
            From our humble beginnings to becoming a trusted technology partner for businesses across industries
          </p>
        </div>

        {/* Timeline */}
        <div className="relative">
          {/* Vertical line */}
          <div className="absolute left-1/2 transform -translate-x-1/2 w-px h-full bg-gradient-to-b from-blue-500 via-blue-500/50 to-transparent hidden lg:block"></div>

          <div className="space-y-12 lg:space-y-24">
            {milestones.map((milestone, index) => (
              <div
                key={milestone.id}
                className={`relative animate-on-scroll animate-in-delay-${index + 1}`}
              >
                <div className={`flex flex-col lg:flex-row items-center gap-8 ${
                  index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
                }`}>
                  {/* Content */}
                  <div className="flex-1 lg:text-right lg:pr-12">
                    <div className={`bg-gray-950 border border-zinc-800 rounded-xl p-8 hover:border-blue-500 transition-all duration-300 ${
                      index % 2 === 0 ? '' : 'lg:text-left lg:pr-0 lg:pl-12'
                    }`}>
                      <div className="text-blue-400 font-bold text-sm mb-2">{milestone.year}</div>
                      <h3 className="text-2xl font-bold text-white mb-3">{milestone.title}</h3>
                      <p className="text-gray-400">{milestone.description}</p>
                    </div>
                  </div>

                  {/* Center icon */}
                  <div className="relative z-10 flex-shrink-0">
                    <div className="w-16 h-16 rounded-full bg-blue-500 flex items-center justify-center text-white border-4 border-gray-900">
                      <Icon name={milestone.icon as any} size={28} />
                    </div>
                  </div>

                  {/* Spacer */}
                  <div className="flex-1 hidden lg:block"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}